import { Collapse } from '@chakra-ui/react';
import { ChevronDownIcon, DateIcon, EyeIcon, ProfileImageIcon } from '@sketchnote/icons';
import { Avatar, AvatarGroup } from '@sketchnote/shared-components';
import cn from 'classnames';
import { format, parseISO } from 'date-fns';
import * as React from 'react';
import { useBoardStore } from 'src/features/boards';
import { calculateTimeInHours } from 'src/utils/date-time-functions';
import { getFormattedValue } from 'src/utils/getFormattedValue';

import { EMPTY_STATE_IMAGES } from '../../../../../design-system/src/shared/constants';
import { LogUser, TaskData, useTimesheetStore } from '../store/useTimesheetStore';
import { TimesheetUser } from './TaskTimelogRow';
const { useState, useEffect } = React;

export const Timesheet = () => {
  const boardsStore = useBoardStore();
  const { getTimesheet, timelogs } = useTimesheetStore();

  useEffect(() => {
    if (boardsStore.id) {
      getTimesheet(boardsStore.id);
    }
  }, [boardsStore.id]);

  return (
    <div
      className="flex justify-center bg-gray-100 w-full"
      style={{ minHeight: 'calc((100vh - 314px))' }}
    >
      {timelogs?.length > 0 ? (
        <div className="flex flex-col p-16 pt-6 overflow-scroll">
          <div className="flex mb-10 justify-between object-center w-full px-24 bg-gray-100 max-w-1440">
            <span className="text-sm text-gray-600 font-medium w-172 min-w-172 mr-15">
              Task title
            </span>
            <span className="text-sm text-gray-600 font-medium w-172 min-w-172">Task key</span>
            <span className="text-sm text-gray-600 font-medium w-172 min-w-172">Assigned date</span>
            <span className="text-sm text-gray-600 font-medium w-172 min-w-172">Assigned to</span>
            <span className="text-sm text-gray-600 font-medium w-172 min-w-172">
              Estimated time
            </span>
            <span className="text-sm text-gray-600 font-medium w-172 min-w-172">
              Total time tracked
            </span>
            <span className="text-sm text-gray-600 font-medium w-172 min-w-172">
              Non billable hours
            </span>
            <span className="text-sm text-gray-600 font-medium w-172 min-w-172">
              Billable hours
            </span>
          </div>
          <div className="flex flex-col w-full">
            {timelogs.map((timelog, index) => {
              return <TimesheetTask key={index} timelogTask={timelog} />;
            })}
          </div>
        </div>
      ) : (
        <div className="py-24">
          <div className="w-900 mx-auto py-80 items-center justify-center rounded-md bg-gray-160 h-full">
            <EmptyState
              contents={['No tasks have been created yet in this file']}
              src={EMPTY_STATE_IMAGES.timesheet}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const EmptyState = ({ contents, src }: { contents: string[]; src: string }) => (
  <div className="flex justify-center text-center p-24 text-gray-999 bg-gray-160">
    <div className="min-h-144">
      <img src={src} width="304px" height="150px" className="mb-8" alt="" />
      {contents.map((obj, index) => (
        <p key={`${obj}${index}`}>{obj}</p>
      ))}
    </div>
  </div>
);

export const formatDateLight = (dates) => {
  const dateFormat = 'MMM d, yyyy';
  if (dates.length === 2 && dates?.[0] && dates?.[1]) {
    return `${format(parseISO(dates[0]), dateFormat)} - ${format(parseISO(dates[1]), dateFormat)}`;
  } else if (dates.length === 1 && dates?.[0]) {
    return format(parseISO(dates[0]), dateFormat);
  }
  return '';
};

const TimesheetTask = ({ timelogTask }: { timelogTask: TaskData }) => {
  const [selectedUser, setSelectedUser] = useState(0);
  const [collapse, setCollapse] = useState(false);

  let totalTimeTracked = 0;

  const getTotalTimeTracked = (logs: LogUser[]): string => {
    if (logs?.length) {
      for (const item of logs) {
        let total = 0;
        if (item?.logs) {
          for (const log of item?.logs) {
            total +=
              parseInt(log?.duration?.hours || '0') * 60 + parseInt(log?.duration?.minutes || '0');
          }
        }
        totalTimeTracked += total;
      }
    }
    if (totalTimeTracked !== 0)
      return calculateTimeInHours({ time: totalTimeTracked, format: 'string' });
    else return '';
  };

  const getBillableHours = (logs: LogUser[]): { billable: string; nonBillable: string } => {
    let billable = '',
      nonBillable = '';
    let billableHours = 0;
    let nonbillableHours = 0;
    if (logs?.length) {
      for (const item of logs) {
        if (item?.logs) {
          for (const log of item?.logs) {
            if (log.billable) {
              billableHours +=
                parseInt(log?.duration?.hours || '0') * 60 +
                parseInt(log?.duration?.minutes || '0');
            } else {
              nonbillableHours +=
                parseInt(log?.duration?.hours || '0') * 60 +
                parseInt(log?.duration?.minutes || '0');
            }
          }
        }
      }
    }
    if (billableHours !== 0)
      billable = calculateTimeInHours({ time: billableHours, format: 'string' });
    if (nonbillableHours !== 0)
      nonBillable = calculateTimeInHours({ time: nonbillableHours, format: 'string' });

    return { billable, nonBillable };
  };

  const timeTrackedStr = getTotalTimeTracked(timelogTask.logs);
  const { billable, nonBillable } = getBillableHours(timelogTask.logs);

  return (
    <div
      className=""
      style={{
        width: 'fit-content',
      }}
    >
      <div
        style={{
          boxShadow: '0px 2px 12px rgba(19, 23, 32, 0.08)',
          border: '1px solid #EDEDED',
        }}
        className="bg-white px-20 mr-20 py-12 items-center justify-between shadow-lg rounded-md cursor-pointer text-left mb-8"
        onClick={(e) => {
          setCollapse(!collapse);
        }}
      >
        <div className="flex w-full items-center">
          <div
            role="button"
            className="justify-between"
            onClick={(e) => {
              setCollapse(!collapse);
            }}
          >
            <div className="flex">
              <span
                style={{
                  transform: collapse ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'all 0.5s',
                }}
                className={'mt-4 block mr-6'}
              >
                <ChevronDownIcon />
              </span>
            </div>
          </div>
          <div className="flex w-172 pr-6">
            <div
              className={cn('text-sm font-medium pt-4 truncate text-gray-999', {
                'text-gray-500': !timelogTask.name,
              })}
            >
              {getFormattedValue(timelogTask.name) || 'Untitled'}
            </div>
          </div>
          <div className="w-172">
            <div className={cn('text-xs font-medium pt-4 truncate text-gray-500')}>
              {timelogTask.key}#{timelogTask.number}
            </div>
          </div>
          <div className="w-172">
            {timelogTask.assignDate?.length ? (
              <span className="mt-2 text-xs text-gray-500">
                <span className="leading-22">{formatDateLight(timelogTask.assignDate)}</span>
              </span>
            ) : (
              <DateIcon wrapperClass="border-2 border-gray-400 text-gray-400 border-dotted p-5 rounded-full" />
            )}
          </div>

          <div className="flex w-172">
            {timelogTask.assignee?.length ? (
              <>
                <AvatarGroup maxCount={1}>
                  {timelogTask.assignee.map((obj) => (
                    <Avatar
                      name={cn(obj.firstname, obj.lastname)}
                      src={obj.avatar}
                      type="photo"
                      key={obj.memberId}
                      wrapperClassName="border-2 border-white"
                    />
                  ))}
                </AvatarGroup>
                {timelogTask.assignee.length > 2 && (
                  <Avatar
                    name={timelogTask.assignee.length - 2}
                    tooltip={false}
                    type={'initials'}
                    src={''}
                    wrapperClassName="-ml-10 border-white border-2 transform transition duration-500 hover:scale-125"
                  />
                )}
              </>
            ) : (
              <span>
                <ProfileImageIcon wrapperClass="border-2 border-gray-400 text-gray-400 border-dotted p-5  rounded-full" />
              </span>
            )}
          </div>
          <div className="flex w-172 text-gray-500 text-xs font-medium title">
            {timelogTask.total_estimate ? (
              `${timelogTask.total_estimate}`
            ) : (
              <div className="bg-gray-500 w-12 h-4 rounded-sm my-auto" />
            )}
          </div>
          {timeTrackedStr !== '' ? (
            <div className="flex w-172 text-gray-500 text-xs font-medium">{timeTrackedStr}</div>
          ) : (
            <div className="flex min-w-172">
              <div className="bg-gray-500 w-12 h-4 rounded-sm my-auto" />
            </div>
          )}
          {nonBillable !== '' ? (
            <div className="flex w-172 text-gray-500 text-xs font-medium">{nonBillable}</div>
          ) : (
            <div className="flex w-172">
              <div className="bg-gray-500 w-12 h-4 rounded-sm my-auto" />
            </div>
          )}
          {billable !== '' ? (
            <div className="flex w-172 text-gray-500 text-xs font-medium">{billable}</div>
          ) : (
            <div className="flex w-172">
              <div className="bg-gray-500 w-12 h-4 rounded-sm my-auto" />
            </div>
          )}
          {timelogTask.watch ? (
            <button className="flex text-gray-500 px-10 py-5">
              <EyeIcon className="text-sm" />
            </button>
          ) : (
            <div className="flex px-18"></div>
          )}
        </div>
      </div>
      <div className="w-full bg-gray-199">
        {timelogTask?.logs &&
          timelogTask?.logs.map((member, index) => {
            return (
              <Collapse in={collapse} className="" key={index} style={{ overflow: 'auto' }}>
                <div
                  className="mb-8"
                  onClick={() => {
                    setSelectedUser(index);
                  }}
                >
                  {
                    <TimesheetUser
                      showLog={selectedUser === index ? true : false}
                      taskKey={`${timelogTask.key}#${timelogTask.number}`}
                      assignDate={timelogTask.assignDate}
                      logUser={member}
                    />
                  }
                </div>
              </Collapse>
            );
          })}
      </div>
    </div>
  );
};
